import { Button, Heading1, Icon } from '@hap/components'
import { IMPERSONATION_IDLE_TIMEOUT_MS } from '@hap/constants'
import { useMutation } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { Suspense } from 'react'
import { z } from 'zod'

import { AppLoader } from '~/components/loader'
import { setSessionToken, useSessionRefresher } from '~/features/auth'
import { LinkButton, LinkButtonHref } from '~/features/navigation'
import { useCurrentUser } from '~/hooks'
import { ErrorBoundary, PageLevelError } from '~/modules/error-boundary'
import { useTRPC } from '~/services/trpc'
import { Nav } from '~/templates/main'
import { PublicTemplate } from '~/templates/public'

const searchParamsSchema = z.object({
  qid: z.string().optional(),
  ieq: z.string().optional(),
  p: z.number().optional(),
  r: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/$alias/_app')({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
  notFoundComponent: () => <div>Not Found!!</div>,
})

const IDLE_TIMEOUT_MINUTES = Math.round(
  IMPERSONATION_IDLE_TIMEOUT_MS / (60 * 1000),
)

function RouteComponent() {
  const trpc = useTRPC()
  useSessionRefresher()
  const currentUser = useCurrentUser()
  const { mutateAsync: stopImpersonation, isPending: isStoppingImpersonation } =
    useMutation(
      trpc.session.stopImpersonation.mutationOptions({
        onSuccess: ({ token }) => {
          setSessionToken(token)
          window.location.reload()
        },
      }),
    )

  if (!currentUser) return <AppLoader />

  if (currentUser.permissions.length === 0) {
    return (
      <PublicTemplate>
        <div className="bg-accent flex flex-col p-10">
          <Icon name="x-circle" className="mx-auto mb-4 size-10" />
          <hgroup className="mb-10 space-y-2 text-center">
            <Heading1>Access Denied</Heading1>
            <p>
              If you think this is a mistake, please
              <br />
              contact support or your account manager.
            </p>
          </hgroup>
          <div className="grid gap-2">
            <LinkButtonHref href="mailto:help@happitu.com">
              Contact Support
            </LinkButtonHref>
            <LinkButton to="/logout" variant="ghost">
              Log out
            </LinkButton>
          </div>
        </div>
      </PublicTemplate>
    )
  }

  return (
    <div className="flex h-dvh flex-col">
      {currentUser?.impersonation && (
        <div className="bg-warn/10 shrink-0 border-b px-4 py-2">
          <div className="mx-auto flex w-full items-center justify-between gap-3">
            <p>
              <strong>Impersonating</strong> You will return to your account
              after {IDLE_TIMEOUT_MINUTES} minutes of inactivity.
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={isStoppingImpersonation}
              onClick={async () => {
                await stopImpersonation()
              }}
            >
              Return to your account
            </Button>
          </div>
        </div>
      )}
      <div className="flex h-dvh shrink grow items-stretch">
        <Nav />

        <div className="m-2 flex shrink grow flex-col overflow-auto rounded-md border">
          <Suspense fallback={<AppLoader />}>
            <ErrorBoundary errorComponent={<PageLevelError />}>
              <Outlet />
            </ErrorBoundary>
          </Suspense>
        </div>
      </div>
    </div>
  )
}
